/**
 * Base Scheduler
 * 
 * Core scheduling infrastructure providing common patterns, configuration management,
 * event handling, and error handling for all scheduler implementations.
 */

import { Device } from '@capacitor/device';
import { isNativeApp } from '@/utils/platformUtils';

/**
 * Sync schedule configuration
 */
export interface SyncScheduleConfig {
  // Basic scheduling
  enableScheduling: boolean;
  defaultInterval: number; // milliseconds
  maxInterval: number; // milliseconds
  minInterval: number; // milliseconds
  
  // Battery optimization
  respectBatteryLevel: boolean;
  lowBatteryThreshold: number; // percentage
  criticalBatteryThreshold: number; // percentage
  batteryOptimizedInterval: number; // milliseconds
  
  // Data usage optimization
  respectDataUsage: boolean;
  preferWifiForLargeSync: boolean;
  maxCellularSyncSize: number; // bytes
  
  // Time-based scheduling
  enableQuietHours: boolean;
  quietHoursStart: string; // HH:MM format
  quietHoursEnd: string; // HH:MM format
  quietHoursInterval: number; // milliseconds
  
  // Adaptive scheduling
  enableAdaptiveScheduling: boolean;
  adaptToUsagePatterns: boolean;
  maxAdaptiveInterval: number; // milliseconds
  
  // Priority scheduling
  enablePrioritySync: boolean;
  highPriorityInterval: number; // milliseconds
  normalPriorityInterval: number; // milliseconds
  lowPriorityInterval: number; // milliseconds
}

/**
 * Sync priority levels
 */
export type SyncPriority = 'high' | 'normal' | 'low';

/**
 * Sync schedule events
 */
export type ScheduleEvent = 
  | { type: 'sync_scheduled'; data: { nextSyncTime: string; priority: SyncPriority; reason: string } }
  | { type: 'sync_postponed'; data: { reason: string; newTime: string; originalTime: string } }
  | { type: 'schedule_adapted'; data: { oldInterval: number; newInterval: number; reason: string } }
  | { type: 'quiet_hours_started'; data: { duration: number } }
  | { type: 'quiet_hours_ended'; data: { missedSyncs: number } }
  | { type: 'battery_optimization_activated'; data: { batteryLevel: number; newInterval: number } }
  | { type: 'data_optimization_activated'; data: { connectionType: string; reason: string } };

export type ScheduleEventListener = (event: ScheduleEvent) => void;

/**
 * Usage pattern data
 */
export interface UsagePattern {
  hourlyActivity: number[]; // 24 hours
  dailyActivity: number[]; // 7 days
  peakUsageHours: number[];
  lowUsageHours: number[];
  averageSessionDuration: number;
  lastUpdated: string;
}

/**
 * Scheduled sync task
 */
export interface ScheduledSync {
  id: string;
  scheduledTime: string;
  priority: SyncPriority;
  reason: string;
  attempts: number;
  maxAttempts: number;
  data?: unknown;
}

/**
 * Base Scheduler Class
 * 
 * Provides common scheduling infrastructure including configuration management,
 * event handling, initialization patterns, and error handling.
 */
export abstract class BaseScheduler {
  protected config: SyncScheduleConfig;
  protected eventListeners: Set<ScheduleEventListener> = new Set();
  protected isInitialized = false;
  
  // Device info
  protected batteryLevel: number = 100;
  
  // Usage patterns
  protected usagePattern: UsagePattern = {
    hourlyActivity: new Array(24).fill(0),
    dailyActivity: new Array(7).fill(0),
    peakUsageHours: [],
    lowUsageHours: [],
    averageSessionDuration: 0,
    lastUpdated: new Date().toISOString()
  };

  constructor(config: Partial<SyncScheduleConfig> = {}) {
    this.config = {
      enableScheduling: true,
      defaultInterval: 300000, // 5 minutes
      maxInterval: 3600000, // 1 hour
      minInterval: 60000, // 1 minute
      respectBatteryLevel: true,
      lowBatteryThreshold: 20,
      criticalBatteryThreshold: 10,
      batteryOptimizedInterval: 900000, // 15 minutes
      respectDataUsage: true,
      preferWifiForLargeSync: true,
      maxCellularSyncSize: 1048576, // 1MB
      enableQuietHours: true,
      quietHoursStart: '22:00',
      quietHoursEnd: '07:00',
      quietHoursInterval: 1800000, // 30 minutes
      enableAdaptiveScheduling: true,
      adaptToUsagePatterns: true,
      maxAdaptiveInterval: 1800000, // 30 minutes
      enablePrioritySync: true,
      highPriorityInterval: 60000, // 1 minute
      normalPriorityInterval: 300000, // 5 minutes
      lowPriorityInterval: 900000, // 15 minutes
      ...config
    };
  }

  /**
   * Initialize the scheduler
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    // Only initialize on native platforms
    if (!isNativeApp()) {
      console.log('📅 Base Scheduler: Web platform detected, skipping initialization');
      return;
    }

    try {
      console.log('📅 Initializing Base Scheduler...');

      // Get initial battery level
      try {
        const batteryInfo = await Device.getBatteryInfo();
        this.batteryLevel = batteryInfo.batteryLevel || 100;
      } catch (error) {
        console.warn('Could not get battery info:', error);
      }

      // Load usage patterns from storage
      await this.loadUsagePatterns();

      // Call subclass initialization
      await this.initializeSubclass();

      this.isInitialized = true;
      console.log('✅ Base Scheduler initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize Base Scheduler:', error);
      throw error;
    }
  }

  /**
   * Subclass-specific initialization
   */
  protected abstract initializeSubclass(): Promise<void>;

  /**
   * Add event listener
   */
  addEventListener(listener: ScheduleEventListener): void {
    this.eventListeners.add(listener);
  }

  /**
   * Remove event listener
   */
  removeEventListener(listener: ScheduleEventListener): void {
    this.eventListeners.delete(listener);
  }

  /**
   * Emit event to all listeners
   */
  protected emitEvent(event: ScheduleEvent): void {
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in schedule event listener:', error);
      }
    });
  }

  /**
   * Update battery level
   */
  updateBatteryLevel(level: number): void {
    this.batteryLevel = level;
    this.onBatteryLevelChanged(level);
  }

  /**
   * Handle battery level changes (to be implemented by subclasses)
   */
  protected abstract onBatteryLevelChanged(level: number): void;

  /**
   * Record usage activity
   */
  recordActivity(): void {
    if (!this.config.adaptToUsagePatterns) return;

    const now = new Date();
    const hour = now.getHours();
    const day = now.getDay();
    
    // Increment activity counters
    this.usagePattern.hourlyActivity[hour] += 1;
    this.usagePattern.dailyActivity[day] += 1;
    this.usagePattern.lastUpdated = now.toISOString();
    
    // Save updated patterns
    this.saveUsagePatterns();
  }

  /**
   * Load usage patterns from storage
   */
  protected async loadUsagePatterns(): Promise<void> {
    try {
      // In a real implementation, this would load from persistent storage
      // For now, we'll use default patterns
      console.log('📊 Loading usage patterns...');
    } catch (error) {
      console.warn('Failed to load usage patterns:', error);
    }
  }

  /**
   * Save usage patterns to storage
   */
  protected async saveUsagePatterns(): Promise<void> {
    try {
      // In a real implementation, this would save to persistent storage
      console.log('📊 Saving usage patterns...');
    } catch (error) {
      console.warn('Failed to save usage patterns:', error);
    }
  }

  /**
   * Get usage patterns
   */
  getUsagePatterns(): UsagePattern {
    return { ...this.usagePattern };
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<SyncScheduleConfig>): void {
    this.config = { ...this.config, ...updates };
    this.onConfigUpdated(updates);
  }

  /**
   * Handle configuration updates (to be implemented by subclasses)
   */
  protected abstract onConfigUpdated(updates: Partial<SyncScheduleConfig>): void;

  /**
   * Get current configuration
   */
  getConfig(): SyncScheduleConfig {
    return { ...this.config };
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    this.eventListeners.clear();
    this.isInitialized = false;
    this.cleanupSubclass();
    console.log('🧹 Base Scheduler cleaned up');
  }

  /**
   * Subclass-specific cleanup
   */
  protected abstract cleanupSubclass(): void;
}
